/**
 * 年次税制ルール（バックエンド `com.smarttax.assistant.tax.TaxRules` /
 * `TaxRulesRegistry` の移植）。
 *
 * 憲章 I（単一情報源と年次分離）に従い、年分ごとの控除額・税率・所得要件を
 * 1 箇所に集約する。控除額表は国税庁 No.1191 / No.1195 / No.1180 / No.1175 および
 * 総務省資料の一次情報に基づく（specs/002-tax-deduction-fixes/research.md）。
 *
 * 金額はすべて円単位の整数で扱い、端数は円未満切捨て（Math.floor）とする。
 */

import type { HousingAcquisitionType, HousingLoanCategory, SalaryProfile, TaxRules } from './types';

/** 未対応の所得年が指定されたことを表す例外。 */
export class UnsupportedTaxYearError extends Error {
  readonly taxYear: number;

  constructor(taxYear: number) {
    super(`未対応の年分です: ${taxYear}年分（対応年: ${supportedYears().join(', ')}）`);
    this.name = 'UnsupportedTaxYearError';
    this.taxYear = taxYear;
  }
}

// 所得税の速算表（全対象年で共通）。
const INCOME_TAX_BRACKETS = [1950000, 3300000, 6950000, 9000000, 18000000, 40000000];
const INCOME_TAX_RATES = [0.05, 0.1, 0.2, 0.23, 0.33, 0.4, 0.45];
const INCOME_TAX_DEDUCTIONS = [0, 97500, 427500, 636000, 1536000, 2796000, 4796000];

// 配偶者控除（本人の合計所得金額による段階）。国税庁 No.1191。
// 控除額表は現状すべての対象年で同額のため共有する。将来金額が変わる年分は
// 年別にビルドする（バックエンド TaxRulesRegistry と同様）。
const SPOUSE_DEDUCTION_LIMITS = [9000000, 9500000, 10000000];
const SPOUSE_DEDUCTION_INCOME = [380000, 260000, 130000];
const SPOUSE_DEDUCTION_RESIDENT = [330000, 220000, 110000];

// 配偶者特別控除（配偶者の合計所得金額の上限で区切る。下限は年分の所得要件）。
const SPOUSE_SPECIAL_LIMITS = [950000, 1000000, 1050000, 1100000, 1150000, 1200000, 1250000, 1300000, 1330000];
const SPOUSE_SPECIAL_INCOME = [
  [380000, 260000, 130000],
  [360000, 240000, 120000],
  [310000, 210000, 110000],
  [260000, 180000, 90000],
  [210000, 140000, 70000],
  [160000, 110000, 60000],
  [110000, 80000, 40000],
  [60000, 40000, 20000],
  [30000, 20000, 10000],
];
const SPOUSE_SPECIAL_RESIDENT = [
  [330000, 220000, 110000],
  [330000, 220000, 110000],
  [310000, 210000, 110000],
  [260000, 180000, 90000],
  [210000, 140000, 70000],
  [160000, 110000, 60000],
  [110000, 80000, 40000],
  [60000, 40000, 20000],
  [30000, 20000, 10000],
];

// 扶養控除（区分別）。国税庁 No.1180 / 総務省資料。
const DEPENDENT_INCOME = 380000;
const SPECIFIC_DEPENDENT_INCOME = 630000;
const ELDERLY_DEPENDENT_INCOME = 480000;
const ELDERLY_LIVING_TOGETHER_INCOME = 580000;
const DEPENDENT_RESIDENT = 330000;
const SPECIFIC_DEPENDENT_RESIDENT = 450000;
const ELDERLY_DEPENDENT_RESIDENT = 380000;
const ELDERLY_LIVING_TOGETHER_RESIDENT = 450000;

// 老人控除対象配偶者（70歳以上）の配偶者控除額。国税庁 No.1191。
const SPOUSE_DEDUCTION_INCOME_ELDERLY = [480000, 320000, 160000];
const SPOUSE_DEDUCTION_RESIDENT_ELDERLY = [380000, 260000, 130000];

// 特定親族特別控除（令和8年分以後・所得税のみ）。国税庁 No.1177。
// 特定親族の合計所得金額 62 万円超 123 万円以下の段階表。
const SPECIFIC_RELATIVE_LIMITS = [
  850000, 900000, 950000, 1000000, 1050000, 1100000, 1150000, 1200000, 1230000,
];
const SPECIFIC_RELATIVE_AMOUNTS = [630000, 610000, 510000, 410000, 310000, 210000, 110000, 60000, 30000];

// 特定親族特別控除（住民税）。令和8年度課税（所得年2025）以後に適用され、下限は年分の
// 住民税扶養所得要件に一致する（令和8年度=58万円、令和9年度=62万円）。総務省・自治体周知。
const SPECIFIC_RELATIVE_RESIDENT_LIMITS = [
  950000, 1000000, 1050000, 1100000, 1150000, 1200000, 1230000,
];
const SPECIFIC_RELATIVE_RESIDENT_AMOUNTS = [450000, 410000, 310000, 210000, 110000, 60000, 30000];

// 配偶者控除の人的控除額の差（調整控除）。総務省・自治体周知。
// 配偶者特別控除の差は令和8年度課税（所得年2025）以後は 0。
const SPOUSE_DIFFERENCE = [50000, 40000, 20000];
const SPOUSE_DIFFERENCE_ELDERLY = [100000, 60000, 30000];

// 生命保険料控除（新制度）。国税庁 No.1140 / 総務省資料。
const LIFE_INSURANCE_INCOME_TOTAL_LIMIT = 120000;
const LIFE_INSURANCE_RESIDENT_TOTAL_LIMIT = 70000;
const LIFE_INSURANCE_RESIDENT_LIMIT = 28000;

// 調整控除・非課税判定（標準的な1級地）。総務省資料。
const ADJUSTMENT_BASIC_DIFFERENCE = 50000;
const ADJUSTMENT_CREDIT_RATE_DENOMINATOR = 20; // 5%
const ADJUSTMENT_CREDIT_MINIMUM = 2500;
const ADJUSTMENT_CREDIT_THRESHOLD_INCOME = 2000000;
const ADJUSTMENT_CREDIT_TOTAL_INCOME_LIMIT = 25000000;
const RESIDENT_NON_TAXABLE_BASE = 350000;
const RESIDENT_NON_TAXABLE_SINGLE = 450000;
const RESIDENT_INCOME_LEVY_ADDITION = 420000;
const RESIDENT_EQUAL_LEVY_ADDITION = 310000;

// 勤労学生控除。国税庁 No.1175 / 総務省資料。
const WORKING_STUDENT_INCOME = 270000;
const WORKING_STUDENT_RESIDENT = 260000;

// --- 住宅ローン控除（住宅借入金等特別控除。#63） ---
// 出典: 国税庁 No.1211-1 / No.1211-3、総務省・自治体（横浜市「税額控除(令和8年度課税以降）」）。
// 居住年ごとの控除対象借入限度額（円）。居住年は「居住の用に供した年」。
// 控除率は令和4年以降入居で 0.7%。控除額は 100 円未満切捨て。
const HOUSING_LOAN_RATE = 0.007;
const HOUSING_LOAN_INCOME_REQUIREMENT = 20000000;
const HOUSING_LOAN_MIN_MOVE_IN_YEAR = 2022;
const HOUSING_LOAN_MAX_MOVE_IN_YEAR = 2030;
// 住民税の控除限度額の計算基礎に加算する所得税の基礎控除の基準額（48万円）。
const HOUSING_LOAN_RESIDENT_BASE_DEDUCTION = 480000;

/**
 * 新築等（新築）の控除対象借入限度額（円）。国税庁 No.1211-1。
 *
 * | 居住年 | 認定長期優良・低炭素 | ZEH水準 | 省エネ基準適合 | その他 |
 * |---|---|---|---|---|
 * | 令和4・5 | 5,000万 | 4,500万 | 4,000万 | 3,000万 |
 * | 令和6・7 | 4,500万（子育て等 5,000万） | 3,500万（同 4,500万） | 3,000万（同 4,000万） | 対象外 |
 * | 令和8・9 | 4,500万（子育て等 5,000万） | 3,500万（同 4,500万） | 2,000万（同 3,000万） | 対象外 |
 * | 令和10〜12 | 4,500万（子育て等 5,000万） | 3,500万（同 4,500万） | 対象外（注5の経過措置を除く） | 対象外 |
 *
 * 買取再販住宅は区分が異なる（国税庁 No.1211-2）ため、本実装では非対応（新築等として扱わない）。
 */
function housingLoanNewBuildLimit(
  moveInYear: number,
  category: HousingLoanCategory,
  childRearing: boolean,
): number {
  if (moveInYear <= 2023) {
    switch (category) {
      case 'LONG_TERM':
        return 50000000;
      case 'ZEH':
        return 45000000;
      case 'ENERGY_SAVING':
        return 40000000;
      case 'OTHER':
      default:
        return 30000000;
    }
  }
  if (moveInYear <= 2025) {
    switch (category) {
      case 'LONG_TERM':
        return childRearing ? 50000000 : 45000000;
      case 'ZEH':
        return childRearing ? 45000000 : 35000000;
      case 'ENERGY_SAVING':
        return childRearing ? 40000000 : 30000000;
      case 'OTHER':
      default:
        // 令和6年以降に建築確認を受けた省エネ基準不適合の新築等は対象外。
        return 0;
    }
  }
  switch (category) {
    case 'LONG_TERM':
      return childRearing ? 50000000 : 45000000;
    case 'ZEH':
      return childRearing ? 45000000 : 35000000;
    case 'ENERGY_SAVING':
      // 令和8・9年は 2,000万（子育て等 3,000万）。令和10〜12年は 0 年（対象外、
      // 注5の経過措置は未対応）。
      return moveInYear <= 2027 ? (childRearing ? 30000000 : 20000000) : 0;
    case 'OTHER':
    default:
      return 0;
  }
}

/**
 * 既存（中古）住宅の控除対象借入限度額（円）。国税庁 No.1211-3。
 *
 * | 居住年 | 認定長期優良・低炭素 / ZEH / 省エネ | その他 |
 * |---|---|---|
 * | 令和4〜7 | 3,000万（区分にかかわらず子育て等の上乗せなし） | 2,000万 |
 * | 令和8〜12 | 認定・ZEH 3,500万（子育て等 4,500万）、省エネ 2,000万（同 3,000万） | 2,000万 |
 */
function housingLoanUsedLimit(
  moveInYear: number,
  category: HousingLoanCategory,
  childRearing: boolean,
): number {
  if (moveInYear <= 2025) {
    switch (category) {
      case 'LONG_TERM':
      case 'ZEH':
      case 'ENERGY_SAVING':
        return 30000000;
      case 'OTHER':
      default:
        return 20000000;
    }
  }
  switch (category) {
    case 'LONG_TERM':
    case 'ZEH':
      return childRearing ? 45000000 : 35000000;
    case 'ENERGY_SAVING':
      return childRearing ? 30000000 : 20000000;
    case 'OTHER':
    default:
      return 20000000;
  }
}

function housingLoanBorrowingLimit(
  moveInYear: number,
  acquisition: HousingAcquisitionType,
  category: HousingLoanCategory,
  childRearing: boolean,
): number {
  if (moveInYear <= 0) {
    return 0;
  }
  if (acquisition === 'USED') {
    return housingLoanUsedLimit(moveInYear, category, childRearing);
  }
  return housingLoanNewBuildLimit(moveInYear, category, childRearing);
}

/**
 * 住民税の住宅借入金等特別税額控除の限度額率。
 * 令和4年入居の特定取得等（消費税率10%・一定期間契約）のみ 7%、
 * それ以外の令和4〜12年入居は 5%（横浜市「税額控除(令和8年度課税以降）」）。
 *
 * 令和3年以前入居は本エンジンの対応範囲外（`housingLoanSupportedMoveInYears`）のため、
 * 月（`moveInMonth`）は 7% の判定に用いない。
 */
function housingLoanResidentCreditLimitRate(
  moveInYear: number,
  _moveInMonth: number,
  specialAcquisition: boolean,
): number {
  if (moveInYear === 2022 && specialAcquisition) {
    return 0.07;
  }
  return 0.05;
}

function housingLoanResidentCreditLimitCap(
  moveInYear: number,
  moveInMonth: number,
  specialAcquisition: boolean,
): number {
  return housingLoanResidentCreditLimitRate(moveInYear, moveInMonth, specialAcquisition) === 0.07
    ? 136500
    : 97500;
}

/** 0.4 / 0.3 / 0.2 / 0.1 のような率（分母 10）の乗算を円未満切捨てで行う。 */
function truncateBy10(value: number, numerator: number): number {
  return Math.floor((value * numerator) / 10);
}

function salaryDeductionReiwa6(gross: number): number {
  if (gross <= 1625000) {
    return Math.min(gross, 550000);
  }
  if (gross <= 1800000) {
    return truncateBy10(gross, 4) - 100000;
  }
  if (gross <= 3600000) {
    return truncateBy10(gross, 3) + 80000;
  }
  if (gross <= 6600000) {
    return truncateBy10(gross, 2) + 440000;
  }
  if (gross <= 8500000) {
    return truncateBy10(gross, 1) + 1100000;
  }
  return 1950000;
}

function salaryDeductionReiwa7(gross: number): number {
  if (gross <= 1900000) {
    return Math.min(gross, 650000);
  }
  if (gross <= 3600000) {
    return truncateBy10(gross, 3) + 80000;
  }
  if (gross <= 6600000) {
    return truncateBy10(gross, 2) + 440000;
  }
  if (gross <= 8500000) {
    return truncateBy10(gross, 1) + 1100000;
  }
  return 1950000;
}

function salaryDeductionReiwa8(gross: number): number {
  // 最低保障額 74 万円（本則 69 万円 + 特例 5 万円）。
  if (gross <= 2200000) {
    return Math.min(gross, 740000);
  }
  if (gross <= 3600000) {
    return truncateBy10(gross, 3) + 80000;
  }
  if (gross <= 6600000) {
    return truncateBy10(gross, 2) + 440000;
  }
  if (gross <= 8500000) {
    return truncateBy10(gross, 1) + 1100000;
  }
  return 1950000;
}

function banded(value: number, limits: number[], amounts: number[]): number {
  for (let i = 0; i < limits.length; i += 1) {
    if (value <= limits[i]) {
      return amounts[i];
    }
  }
  return 0;
}

function bandedRate(value: number, brackets: number[], values: number[]): number {
  for (let i = 0; i < brackets.length; i += 1) {
    if (value <= brackets[i]) {
      return values[i];
    }
  }
  return values[values.length - 1];
}

function taxpayerColumn(income: number): number {
  for (let i = 0; i < SPOUSE_DEDUCTION_LIMITS.length; i += 1) {
    if (income <= SPOUSE_DEDUCTION_LIMITS[i]) {
      return i;
    }
  }
  return -1;
}

function spouseSpecialDeduction(
  taxpayerIncome: number,
  spouseIncome: number,
  amounts: number[][],
  threshold: number,
): number {
  // 事前条件: spouseIncome > threshold（呼び出し元で判定済み）
  if (spouseIncome > SPOUSE_SPECIAL_LIMITS[SPOUSE_SPECIAL_LIMITS.length - 1]) {
    return 0;
  }
  const column = taxpayerColumn(taxpayerIncome);
  if (column < 0) {
    return 0;
  }
  for (let i = 0; i < SPOUSE_SPECIAL_LIMITS.length; i += 1) {
    if (spouseIncome <= SPOUSE_SPECIAL_LIMITS[i]) {
      return amounts[i][column];
    }
  }
  return 0;
}

function elderlyTotal(elderly: number): number {
  return Math.max(0, elderly);
}

/** 同居老親等の人数を老人扶養親族の人数に丸める（内数のため）。 */
function livingTogetherCount(elderly: number, elderlyLivingTogether: number): number {
  return Math.min(Math.max(0, elderlyLivingTogether), elderlyTotal(elderly));
}

function count(amount: number, numberOfPeople: number): number {
  return amount * Math.max(0, numberOfPeople);
}

/** 所得税の生命保険料控除（新制度）の 1 区分分。 */
function lifeInsuranceIncomePart(premium: number): number {
  if (premium <= 0) {
    return 0;
  }
  if (premium <= 20000) {
    return premium;
  }
  if (premium <= 40000) {
    return Math.floor(premium / 2) + 10000;
  }
  if (premium <= 80000) {
    return Math.floor(premium / 4) + 20000;
  }
  return 40000;
}

/**
 * 子育て世帯特例（令和8・9年分）の一般生命保険料控除（所得税）。
 * 通常の速算表を 1.5 倍に引き上げた区分（上限 6 万円）を用いる。
 */
function lifeInsuranceIncomeGeneralChildRearing(premium: number): number {
  if (premium <= 0) {
    return 0;
  }
  if (premium <= 30000) {
    return premium;
  }
  if (premium <= 60000) {
    return Math.floor(premium / 2) + 15000;
  }
  if (premium <= 120000) {
    return Math.floor(premium / 4) + 30000;
  }
  return 60000;
}

/** 住民税の生命保険料控除（新制度）の 1 区分分。 */
function lifeInsuranceResidentPart(premium: number): number {
  if (premium <= 0) {
    return 0;
  }
  if (premium <= 12000) {
    return premium;
  }
  if (premium <= 32000) {
    return Math.floor(premium / 2) + 6000;
  }
  if (premium <= 56000) {
    return Math.floor(premium / 4) + 14000;
  }
  return LIFE_INSURANCE_RESIDENT_LIMIT;
}

/** 特定親族特別控除の段階表。下限は呼び出し元で判定する。 */
function specificRelativeDeduction(income: number, limits: number[], amounts: number[]): number {
  if (income > limits[limits.length - 1]) {
    return 0;
  }
  return banded(income, limits, amounts);
}

/** 住民税の調整控除。 */
function adjustmentCreditAmount(
  taxableIncome: number,
  humanDeductionDifference: number,
  totalIncome: number,
): number {
  if (totalIncome > ADJUSTMENT_CREDIT_TOTAL_INCOME_LIMIT || taxableIncome <= 0) {
    return 0;
  }
  let base: number;
  if (taxableIncome <= ADJUSTMENT_CREDIT_THRESHOLD_INCOME) {
    base = Math.min(humanDeductionDifference, taxableIncome);
  } else {
    base = humanDeductionDifference - (taxableIncome - ADJUSTMENT_CREDIT_THRESHOLD_INCOME);
    // 2,500 円（= 50,000 円 × 5%）を下回る場合は 2,500 円とする。
    if (base < ADJUSTMENT_CREDIT_MINIMUM * ADJUSTMENT_CREDIT_RATE_DENOMINATOR) {
      base = ADJUSTMENT_CREDIT_MINIMUM * ADJUSTMENT_CREDIT_RATE_DENOMINATOR;
    }
  }
  if (base <= 0) {
    return 0;
  }
  return Math.floor(base / ADJUSTMENT_CREDIT_RATE_DENOMINATOR);
}

interface TaxRulesData {
  incomeYear: number;
  salaryProfile: SalaryProfile;
  basicDeductionLimits: number[];
  basicDeductionAmounts: number[];
  residentBasicDeductionLimits: number[];
  residentBasicDeductionAmounts: number[];
  spouseIncomeThreshold: number;
  dependentIncomeThresholdIncome: number;
  dependentIncomeThresholdResident: number;
  /** 特定親族特別控除（所得税）の適用有無（令和8年分以後）。 */
  specificRelativeDeductionEnabled: boolean;
  /** 特定親族特別控除（住民税）の適用有無（令和8年度課税＝所得年2025以後）。 */
  specificRelativeDeductionResidentEnabled: boolean;
  /** 配偶者特別控除の人的控除差の適用有無（令和7年度課税＝所得年2024以前のみ）。 */
  spouseSpecialDifferenceEnabled: boolean;
  /** 子育て世帯の生命保険料控除特例（令和8・9年分限定）。 */
  childRearingLifeInsuranceEnabled: boolean;
  residentTaxRate: number;
  residentEqualLevy: number;
  reconstructionSurtaxRate: number;
  defenseSurtaxRate: number;
}

function createTaxRules(data: TaxRulesData): TaxRules {
  const {
    incomeYear,
    salaryProfile,
    basicDeductionLimits,
    basicDeductionAmounts,
    residentBasicDeductionLimits,
    residentBasicDeductionAmounts,
    spouseIncomeThreshold,
    dependentIncomeThresholdIncome,
    dependentIncomeThresholdResident,
    specificRelativeDeductionEnabled,
    specificRelativeDeductionResidentEnabled,
    spouseSpecialDifferenceEnabled,
    childRearingLifeInsuranceEnabled,
    residentTaxRate,
    residentEqualLevy,
    reconstructionSurtaxRate,
    defenseSurtaxRate,
  } = data;

  // 浮動小数の誤差を避けるため、合算レートは小数第 3 位に丸める（例: 0.011 + 0.010 = 0.021）。
  const surtaxRate = (): number =>
    Math.round((reconstructionSurtaxRate + defenseSurtaxRate) * 1000) / 1000;

  const rules: TaxRules = {
    incomeYear: () => incomeYear,
    residentFiscalYear: () => incomeYear + 1,

    salaryDeduction: (gross: number): number => {
      switch (salaryProfile) {
        case 'REIWA_6':
          return salaryDeductionReiwa6(gross);
        case 'REIWA_7':
          return salaryDeductionReiwa7(gross);
        case 'REIWA_8':
        default:
          return salaryDeductionReiwa8(gross);
      }
    },
    basicDeduction: (totalIncome: number): number =>
      banded(totalIncome, basicDeductionLimits, basicDeductionAmounts),
    residentBasicDeduction: (totalIncome: number): number =>
      banded(totalIncome, residentBasicDeductionLimits, residentBasicDeductionAmounts),

    spouseIncomeThreshold: () => spouseIncomeThreshold,
    spouseSpecialDeductionLimit: () => SPOUSE_SPECIAL_LIMITS[SPOUSE_SPECIAL_LIMITS.length - 1],
    spouseDeduction: (taxpayerIncome: number, spouseIncome: number, elderly = false): number => {
      if (spouseIncome <= spouseIncomeThreshold) {
        return banded(
          taxpayerIncome,
          SPOUSE_DEDUCTION_LIMITS,
          elderly ? SPOUSE_DEDUCTION_INCOME_ELDERLY : SPOUSE_DEDUCTION_INCOME,
        );
      }
      return spouseSpecialDeduction(taxpayerIncome, spouseIncome, SPOUSE_SPECIAL_INCOME, spouseIncomeThreshold);
    },
    residentSpouseDeduction: (taxpayerIncome: number, spouseIncome: number, elderly = false): number => {
      if (spouseIncome <= spouseIncomeThreshold) {
        return banded(
          taxpayerIncome,
          SPOUSE_DEDUCTION_LIMITS,
          elderly ? SPOUSE_DEDUCTION_RESIDENT_ELDERLY : SPOUSE_DEDUCTION_RESIDENT,
        );
      }
      return spouseSpecialDeduction(taxpayerIncome, spouseIncome, SPOUSE_SPECIAL_RESIDENT, spouseIncomeThreshold);
    },

    dependentIncomeThresholdIncome: () => dependentIncomeThresholdIncome,
    dependentIncomeThresholdResident: () => dependentIncomeThresholdResident,

    dependentDeductionIncome: (
      general: number,
      specific: number,
      elderly: number,
      elderlyLivingTogether: number,
    ): number => {
      const livingTogether = livingTogetherCount(elderly, elderlyLivingTogether);
      const elderlyOther = elderlyTotal(elderly) - livingTogether;
      return (
        count(DEPENDENT_INCOME, general) +
        count(SPECIFIC_DEPENDENT_INCOME, specific) +
        count(ELDERLY_DEPENDENT_INCOME, elderlyOther) +
        count(ELDERLY_LIVING_TOGETHER_INCOME, livingTogether)
      );
    },
    dependentDeductionResident: (
      general: number,
      specific: number,
      elderly: number,
      elderlyLivingTogether: number,
    ): number => {
      const livingTogether = livingTogetherCount(elderly, elderlyLivingTogether);
      const elderlyOther = elderlyTotal(elderly) - livingTogether;
      return (
        count(DEPENDENT_RESIDENT, general) +
        count(SPECIFIC_DEPENDENT_RESIDENT, specific) +
        count(ELDERLY_DEPENDENT_RESIDENT, elderlyOther) +
        count(ELDERLY_LIVING_TOGETHER_RESIDENT, livingTogether)
      );
    },

    workingStudentDeductionIncome: () => WORKING_STUDENT_INCOME,
    workingStudentDeductionResident: () => WORKING_STUDENT_RESIDENT,

    specificRelativeDeductionIncome: (specificIncome: number): number => {
      if (!specificRelativeDeductionEnabled || specificIncome <= dependentIncomeThresholdIncome) {
        return 0;
      }
      return specificRelativeDeduction(specificIncome, SPECIFIC_RELATIVE_LIMITS, SPECIFIC_RELATIVE_AMOUNTS);
    },
    specificRelativeDeductionResident: (specificIncome: number): number => {
      if (!specificRelativeDeductionResidentEnabled || specificIncome <= dependentIncomeThresholdResident) {
        return 0;
      }
      return specificRelativeDeduction(
        specificIncome,
        SPECIFIC_RELATIVE_RESIDENT_LIMITS,
        SPECIFIC_RELATIVE_RESIDENT_AMOUNTS,
      );
    },
    spouseDeductionDifference: (taxpayerIncome: number, spouseIncome: number, elderly = false): number => {
      if (spouseIncome <= spouseIncomeThreshold) {
        return banded(
          taxpayerIncome,
          SPOUSE_DEDUCTION_LIMITS,
          elderly ? SPOUSE_DIFFERENCE_ELDERLY : SPOUSE_DIFFERENCE,
        );
      }
      if (!spouseSpecialDifferenceEnabled) {
        // 令和8年度課税（所得年2025）以後、配偶者特別控除の人的控除差はない。
        return 0;
      }
      const incomeAmount = spouseSpecialDeduction(
        taxpayerIncome,
        spouseIncome,
        SPOUSE_SPECIAL_INCOME,
        spouseIncomeThreshold,
      );
      const residentAmount = spouseSpecialDeduction(
        taxpayerIncome,
        spouseIncome,
        SPOUSE_SPECIAL_RESIDENT,
        spouseIncomeThreshold,
      );
      return Math.max(0, incomeAmount - residentAmount);
    },

    lifeInsuranceDeductionIncome: (
      general: number,
      medical: number,
      pension: number,
      childRearing: boolean,
    ): number => {
      const generalDeduction =
        childRearing && childRearingLifeInsuranceEnabled
          ? lifeInsuranceIncomeGeneralChildRearing(general)
          : lifeInsuranceIncomePart(general);
      const total =
        generalDeduction + lifeInsuranceIncomePart(medical) + lifeInsuranceIncomePart(pension);
      return Math.min(total, LIFE_INSURANCE_INCOME_TOTAL_LIMIT);
    },
    lifeInsuranceDeductionResident: (general: number, medical: number, pension: number): number => {
      const total =
        lifeInsuranceResidentPart(general) +
        lifeInsuranceResidentPart(medical) +
        lifeInsuranceResidentPart(pension);
      return Math.min(total, LIFE_INSURANCE_RESIDENT_TOTAL_LIMIT);
    },

    basicDeductionDifference: () => ADJUSTMENT_BASIC_DIFFERENCE,
    adjustmentCredit: (taxableIncome: number, humanDeductionDifference: number, totalIncome: number): number =>
      adjustmentCreditAmount(taxableIncome, humanDeductionDifference, totalIncome),
    residentIncomeLevyNonTaxableLimit: (householdCount: number, hasDependents: boolean): number =>
      hasDependents
        ? RESIDENT_NON_TAXABLE_BASE * householdCount + RESIDENT_INCOME_LEVY_ADDITION
        : RESIDENT_NON_TAXABLE_SINGLE,
    residentEqualLevyNonTaxableLimit: (householdCount: number, hasDependents: boolean): number =>
      hasDependents
        ? RESIDENT_NON_TAXABLE_BASE * householdCount + RESIDENT_EQUAL_LEVY_ADDITION
        : RESIDENT_NON_TAXABLE_SINGLE,

    incomeTaxRate: (taxable: number): number => bandedRate(taxable, INCOME_TAX_BRACKETS, INCOME_TAX_RATES),
    incomeTaxDeduction: (taxable: number): number =>
      bandedRate(taxable, INCOME_TAX_BRACKETS, INCOME_TAX_DEDUCTIONS),
    incomeTaxWithSurtax: (incomeTax: number): number => {
      const surtaxMilli = Math.round(surtaxRate() * 1000);
      return Math.floor((incomeTax * (1000 + surtaxMilli)) / 1000);
    },

    surtaxRate,
    reconstructionSurtaxRate: () => reconstructionSurtaxRate,
    defenseSurtaxRate: () => defenseSurtaxRate,

    residentTaxRate: () => residentTaxRate,
    residentEqualLevy: () => residentEqualLevy,

    housingLoanRate: () => HOUSING_LOAN_RATE,
    housingLoanIncomeRequirement: () => HOUSING_LOAN_INCOME_REQUIREMENT,
    housingLoanSupportedMoveInYears: () => ({
      min: HOUSING_LOAN_MIN_MOVE_IN_YEAR,
      max: HOUSING_LOAN_MAX_MOVE_IN_YEAR,
    }),
    housingLoanBorrowingLimit: (
      moveInYear: number,
      acquisition: HousingAcquisitionType,
      category: HousingLoanCategory,
      childRearing: boolean,
    ): number => housingLoanBorrowingLimit(moveInYear, acquisition, category, childRearing),
    housingLoanResidentCreditLimitRate: (
      moveInYear: number,
      moveInMonth: number,
      specialAcquisition: boolean,
    ): number => housingLoanResidentCreditLimitRate(moveInYear, moveInMonth, specialAcquisition),
    housingLoanResidentCreditLimitCap: (
      moveInYear: number,
      moveInMonth: number,
      specialAcquisition: boolean,
    ): number => housingLoanResidentCreditLimitCap(moveInYear, moveInMonth, specialAcquisition),
    housingLoanResidentCreditBaseAdjustment: (incomeTaxBasicDeduction: number): number =>
      Math.max(0, incomeTaxBasicDeduction - HOUSING_LOAN_RESIDENT_BASE_DEDUCTION),
  };

  return rules;
}

// 所得税の基礎控除（合計所得金額の区分）。区分上限の昇順。
const BASIC_DEDUCTION_2024_LIMITS = [24000000, 24500000, 25000000];
const BASIC_DEDUCTION_2024_AMOUNTS = [480000, 320000, 160000];

const BASIC_DEDUCTION_2025_LIMITS = [1320000, 3360000, 4890000, 6550000, 23500000, 24000000, 24500000, 25000000];
const BASIC_DEDUCTION_2025_AMOUNTS = [950000, 880000, 680000, 630000, 580000, 480000, 320000, 160000];

const BASIC_DEDUCTION_2026_LIMITS = [4890000, 6550000, 23500000, 24000000, 24500000, 25000000];
const BASIC_DEDUCTION_2026_AMOUNTS = [1040000, 670000, 620000, 480000, 320000, 160000];

// 住民税の基礎控除（令和3年度分以後で共通）。
const RESIDENT_BASIC_DEDUCTION_LIMITS = [24000000, 24500000, 25000000];
const RESIDENT_BASIC_DEDUCTION_AMOUNTS = [430000, 290000, 150000];

// 住民税の均等割（道府県民税1,500 + 市町村民税3,500 + 森林環境税1,000）。
const RESIDENT_EQUAL_LEVY = 6000;

/** 対象年が未指定の場合に用いる既定の所得年（令和8年分）。 */
export const DEFAULT_INCOME_YEAR = 2026;

const RULES: ReadonlyMap<number, TaxRules> = new Map<number, TaxRules>([
  [
    2024,
    createTaxRules({
      incomeYear: 2024,
      salaryProfile: 'REIWA_6',
      basicDeductionLimits: BASIC_DEDUCTION_2024_LIMITS,
      basicDeductionAmounts: BASIC_DEDUCTION_2024_AMOUNTS,
      residentBasicDeductionLimits: RESIDENT_BASIC_DEDUCTION_LIMITS,
      residentBasicDeductionAmounts: RESIDENT_BASIC_DEDUCTION_AMOUNTS,
      spouseIncomeThreshold: 480000,
      dependentIncomeThresholdIncome: 480000,
      dependentIncomeThresholdResident: 480000,
      specificRelativeDeductionEnabled: false,
      specificRelativeDeductionResidentEnabled: false,
      spouseSpecialDifferenceEnabled: true,
      childRearingLifeInsuranceEnabled: false,
      residentTaxRate: 0.1,
      residentEqualLevy: RESIDENT_EQUAL_LEVY,
      reconstructionSurtaxRate: 0.021,
      defenseSurtaxRate: 0,
    }),
  ],
  [
    2025,
    createTaxRules({
      incomeYear: 2025,
      salaryProfile: 'REIWA_7',
      basicDeductionLimits: BASIC_DEDUCTION_2025_LIMITS,
      basicDeductionAmounts: BASIC_DEDUCTION_2025_AMOUNTS,
      residentBasicDeductionLimits: RESIDENT_BASIC_DEDUCTION_LIMITS,
      residentBasicDeductionAmounts: RESIDENT_BASIC_DEDUCTION_AMOUNTS,
      spouseIncomeThreshold: 580000,
      dependentIncomeThresholdIncome: 580000,
      dependentIncomeThresholdResident: 580000,
      specificRelativeDeductionEnabled: false,
      specificRelativeDeductionResidentEnabled: true,
      spouseSpecialDifferenceEnabled: false,
      childRearingLifeInsuranceEnabled: false,
      residentTaxRate: 0.1,
      residentEqualLevy: RESIDENT_EQUAL_LEVY,
      reconstructionSurtaxRate: 0.021,
      defenseSurtaxRate: 0,
    }),
  ],
  [
    2026,
    createTaxRules({
      incomeYear: 2026,
      salaryProfile: 'REIWA_8',
      basicDeductionLimits: BASIC_DEDUCTION_2026_LIMITS,
      basicDeductionAmounts: BASIC_DEDUCTION_2026_AMOUNTS,
      residentBasicDeductionLimits: RESIDENT_BASIC_DEDUCTION_LIMITS,
      residentBasicDeductionAmounts: RESIDENT_BASIC_DEDUCTION_AMOUNTS,
      spouseIncomeThreshold: 620000,
      dependentIncomeThresholdIncome: 620000,
      dependentIncomeThresholdResident: 620000,
      specificRelativeDeductionEnabled: true,
      specificRelativeDeductionResidentEnabled: true,
      spouseSpecialDifferenceEnabled: false,
      childRearingLifeInsuranceEnabled: true,
      residentTaxRate: 0.1,
      residentEqualLevy: RESIDENT_EQUAL_LEVY,
      reconstructionSurtaxRate: 0.021,
      defenseSurtaxRate: 0,
    }),
  ],
  [
    2027,
    createTaxRules({
      incomeYear: 2027,
      salaryProfile: 'REIWA_8',
      basicDeductionLimits: BASIC_DEDUCTION_2026_LIMITS,
      basicDeductionAmounts: BASIC_DEDUCTION_2026_AMOUNTS,
      residentBasicDeductionLimits: RESIDENT_BASIC_DEDUCTION_LIMITS,
      residentBasicDeductionAmounts: RESIDENT_BASIC_DEDUCTION_AMOUNTS,
      spouseIncomeThreshold: 620000,
      dependentIncomeThresholdIncome: 620000,
      dependentIncomeThresholdResident: 620000,
      specificRelativeDeductionEnabled: true,
      specificRelativeDeductionResidentEnabled: true,
      spouseSpecialDifferenceEnabled: false,
      childRearingLifeInsuranceEnabled: true,
      residentTaxRate: 0.1,
      residentEqualLevy: RESIDENT_EQUAL_LEVY,
      reconstructionSurtaxRate: 0.011,
      defenseSurtaxRate: 0.01,
    }),
  ],
]);

/** 指定された所得年のルールを返す。未対応の年分の場合は例外を投げる。 */
export function of(incomeYear: number): TaxRules {
  const rules = RULES.get(incomeYear);
  if (!rules) {
    throw new UnsupportedTaxYearError(incomeYear);
  }
  return rules;
}

/** 指定された所得年が対応済みかどうか。 */
export function isSupported(incomeYear: number): boolean {
  return RULES.has(incomeYear);
}

/** 対象年が未指定の場合の既定所得年。 */
export function defaultIncomeYear(): number {
  return DEFAULT_INCOME_YEAR;
}

/** 対応している所得年の集合（昇順）。 */
export function supportedYears(): number[] {
  return Array.from(RULES.keys()).sort((a, b) => a - b);
}

/** 所得年を和暦の年分表記に変換する（例: 2026 → 令和8年）。 */
export function eraYearLabel(incomeYear: number): string {
  if (incomeYear >= 2019) {
    return `令和${incomeYear - 2018}年`;
  }
  return `${incomeYear}年`;
}

/** 画面表示用の適用年分ラベル（例: 令和8年分（2026年））。 */
export function formatIncomeYearLabel(incomeYear: number): string {
  return `${eraYearLabel(incomeYear)}分（${incomeYear}年）`;
}

/** バックエンド TaxRulesRegistry 相当のレジストリ。 */
export const TaxRulesRegistry = {
  of,
  isSupported,
  defaultIncomeYear,
  supportedYears,
};
